// UGC video slider: infinite loop (cards cloned before and after), arrows, drag/swipe, click a side card
// to bring it to the center. Only the active card loads and plays its video, and only while the slider
// is in view. With prefers-reduced-motion the slide is instant and nothing autoplays: tap the center
// card to play or pause it.
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SLIDE_MS = 520;
const EASE = 'cubic-bezier(.22,.8,.36,1)';
const DRAG_START_PX = 4;
const SWIPE_PX = 60;

class MvUgcSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.track = this.querySelector('[data-ugc-track]');
    if (!this.track) return;
    const originals = Array.from(this.track.querySelectorAll('[data-ugc-card]'));
    this.count = originals.length;
    if (!this.count) return;
    this.dataset.ready = 'true';

    this.track.prepend(...originals.map((card) => this.cloneCard(card)));
    this.track.append(...originals.map((card) => this.cloneCard(card)));
    this.cards = Array.from(this.track.children);
    this.active = this.count;
    this.visible = false;
    this.userPlay = false;

    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;
      video.addEventListener('playing', () => video.classList.add('is-playing'));
      video.addEventListener('pause', () => video.classList.remove('is-playing'));
    });

    this.querySelector('[data-ugc-prev]')?.addEventListener('click', () => this.go(this.active - 1));
    this.querySelector('[data-ugc-next]')?.addEventListener('click', () => this.go(this.active + 1));
    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.normalize();
    });
    this.track.addEventListener('click', (event) => this.onClick(event), true);
    this.addEventListener('pointerdown', (event) => this.onDown(event));
    this.addEventListener('pointermove', (event) => this.onMove(event));
    this.addEventListener('pointerup', (event) => this.onUp(event));
    this.addEventListener('pointercancel', () => this.onCancel());
    this.addEventListener('shopify:block:select', (event) => {
      const index = originals.indexOf(event.target.closest('[data-ugc-card]'));
      if (index !== -1) this.go(this.count + index);
    });

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          this.updateVideos();
        });
      }, { threshold: 0.2, rootMargin: '200px 0px' });
      this.intersectionObserver.observe(this);
    } else {
      this.visible = true;
    }

    this.measure();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  cloneCard(card) {
    const clone = card.cloneNode(true);
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }

  measure() {
    // computed width, not offsetWidth: that is rounded (64vw on a phone) and the error adds up per card
    this.cardWidth = parseFloat(getComputedStyle(this.cards[0]).width) || this.cards[0].offsetWidth;
    this.step = this.cardWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
    this.apply(false);
  }

  offsetFor(index) {
    return this.clientWidth / 2 - (index * this.step + this.cardWidth / 2);
  }

  apply(animated) {
    const smooth = animated && !reduceMotion.matches;
    this.track.style.transition = smooth ? `transform ${SLIDE_MS}ms ${EASE}` : 'none';
    this.track.style.transform = `translate3d(${this.offsetFor(this.active)}px, 0, 0)`;
    this.cards.forEach((card, index) => card.classList.toggle('is-active', index === this.active));
    this.updateVideos();
    if (!smooth) this.normalize();
  }

  // Jump back into the middle set without animation, so the loop never runs out of cards.
  normalize() {
    if (this.active >= this.count && this.active < this.count * 2) return;
    this.active = ((this.active % this.count) + this.count) % this.count + this.count;
    this.apply(false);
  }

  go(index) {
    if (index !== this.active) this.userPlay = false;
    this.active = index;
    this.apply(true);
  }

  updateVideos() {
    const autoplay = !reduceMotion.matches || this.userPlay;
    this.cards.forEach((card, index) => {
      const video = card.querySelector('video');
      if (!video) return;
      if (index === this.active && this.visible && autoplay) {
        if (!video.getAttribute('src')) video.src = video.dataset.src;
        const playing = video.play();
        if (playing) playing.catch(() => {});
        return;
      }
      video.pause();
      if (Math.abs(index - this.active) > 1 && video.getAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    });
  }

  onClick(event) {
    if (this.didDrag) {
      event.preventDefault();
      event.stopPropagation();
      this.didDrag = false;
      return;
    }
    const card = event.target.closest('[data-ugc-card]');
    if (!card) return;
    const index = this.cards.indexOf(card);
    if (index === -1) return;
    if (index !== this.active) {
      this.go(index);
    } else if (reduceMotion.matches) {
      this.userPlay = !this.userPlay;
      this.updateVideos();
    }
  }

  onDown(event) {
    if (event.button > 0 || event.target.closest('button')) return;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartActive = this.active;
    this.dragDelta = 0;
    this.didDrag = false;
  }

  onMove(event) {
    if (this.pointerId !== event.pointerId) return;
    this.dragDelta = event.clientX - this.dragStartX;
    if (!this.didDrag) {
      if (Math.abs(this.dragDelta) <= DRAG_START_PX) return;
      this.didDrag = true;
      this.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
      this.track.style.transition = 'none';
    }
    const x = this.offsetFor(this.dragStartActive) + this.dragDelta;
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  onUp(event) {
    if (this.pointerId !== event.pointerId) return;
    this.pointerId = null;
    if (!this.didDrag) return;
    this.track.classList.remove('is-dragging');
    let shift = Math.round(-this.dragDelta / this.step);
    if (shift === 0 && Math.abs(this.dragDelta) > SWIPE_PX) shift = this.dragDelta < 0 ? 1 : -1;
    this.go(this.dragStartActive + shift);
  }

  onCancel() {
    if (this.pointerId == null) return;
    this.pointerId = null;
    this.track.classList.remove('is-dragging');
    this.didDrag = false;
    this.go(this.dragStartActive);
  }
}

if (!customElements.get('mv-ugc-slider')) customElements.define('mv-ugc-slider', MvUgcSlider);
